import { z } from "zod";

export const AttributeSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  labels: z.record(z.coerce.number(), z.string()).optional(),
});

export const VersionConfigSchema = z.object({
  attributeIds: z.array(z.string()),
  minRating: z.number().default(1),
  maxRating: z.number(),
  labelInterval: z.number().min(1).default(1),
}).refine((data) => data.minRating < data.maxRating, {
  message: "minRating must be less than maxRating",
}).refine((data) => {
  if (data.labelInterval === 1) return true;
  return (data.maxRating - data.minRating) % data.labelInterval === 0;
}, {
  message: "(maxRating - minRating) must be a multiple of labelInterval",
});

export const RatingConfigSchema = z.object({
  currentVersion: z.string(),
  versions: z.record(z.string(), VersionConfigSchema),
  defaultLabels: z.record(z.coerce.number(), z.string()),
  attributes: z.array(AttributeSchema),
  themeSet: z.enum(["default", "custom"]).optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  nameLabel: z.string().optional(),
  attributeTerm: z.string().optional(),
  attributeTermPlural: z.string().optional(),
}).refine((data) => {
  // Ensure currentVersion exists in versions
  if (!(data.currentVersion in data.versions)) {
    return false;
  }
  // Ensure all attributeIds in versions exist in attributes
  const validIds = new Set(data.attributes.map(a => a.id));
  for (const v of Object.values(data.versions)) {
    if (!v.attributeIds.every(id => validIds.has(id))) {
      return false;
    }
  }
  return true;
}, {
  message: "Invalid configuration: missing version or attribute ID references.",
});
